import { Controller } from "stimulus";
import "@mux/mux-uploader";

const VIDEO_TYPES = [
  "video/mp4",
  "video/quicktime",
];

const AUDIO_TYPES = [
  "audio/mp3",
  "audio/mpeg",
  "audio/wav",
];

export default class UploadController extends Controller {
  connect() {
    this._uploading = false;
    this._uploadStats = {};

    this.extracting = false;
    if (this.hasEpisodeIdValue && this.episodeIdValue) {
      this.checkExtractionStatusAndMaybePoll();
    }
  }

  showUploadedTargetConnected(el) {
    if (this._uploading) {
      el.hidden = true;
    }
  }

  async muxUploaderTargetConnected(el) {

    el.endpoint = async (file) => {
      const media = file?.type?.startsWith("audio/") ? "audio" : "video";
      const title = file?.name?.replace(/\.[^.]+$/, "");
      const url = new URL(window.location.origin + "/dashboard/channels/episodes/presigned_mux_upload");
      if (this.episodeIdValue) url.searchParams.append("episode", this.episodeIdValue);
      url.searchParams.append("media", media);
      if (title) url.searchParams.append("title", title);
      const response = await fetch(url.toString());
      const data = await response.json();
      this.episodeIdValue = data.episode_id;
      this._pendingMuxUpload = data;

      this.navigateToDetails();
      return data.upload_url;
    }

    el.addEventListener("uploadstart", (e) => this._onUploadStart(e));
    el.addEventListener("progress", (e) => this._onProgress(e));
    el.addEventListener("success", () => this._onSuccess());
    el.addEventListener("error", (e) => this._onError(e));
  }

  async commitMuxUpload() {
    const csrfToken = document.getElementsByName("csrf-token")[0].content;
    const formData = new FormData();
    formData.append("authenticity_token", csrfToken);
    formData.append("episode[mux_upload_id]", this._pendingMuxUpload?.upload_id || "");

    const payload = { method: "PATCH", headers: { "Accept": "application/json" }, body: formData };
    const response = await fetch(`/dashboard/channels/episodes/${this.episodeIdValue}`, payload);

    if (!response.ok) {
      if (this.hasErrorTarget) this.errorTarget.innerText = "Upload commit failed. Please try again.";
      return;
    }

    this._uploading = false;
    this.showUploadingTargets.forEach(t => t.hidden = true);
    this.showUploadedTargets.forEach(t => t.hidden = false);
  }

  navigateToDetails() {
    window.Turbo.visit(`/dashboard/channels/episodes/${this.episodeIdValue}/detail`, { frame: "step" });
  }

  previewVideoTargetConnected(el) {
    if (this._localFile) {
      if (this._localPreviewUrl) URL.revokeObjectURL(this._localPreviewUrl);
      this._localPreviewUrl = URL.createObjectURL(this._localFile);
      el.querySelectorAll("source").forEach(s => s.remove());
      const source = document.createElement("source");
      source.src = this._localPreviewUrl;
      source.type = this._localFile.type;
      el.appendChild(source);
      el.load();
    }

    if (this.hasVideoImageInputTarget) {
      this.videoImageInputTarget.addEventListener("change", () => {
        if (FileReader && this.videoImageInputTarget.files && this.videoImageInputTarget.files.length) {
          var fr = new FileReader();
          fr.onload = function () {
            el.poster = fr.result;
            el.load();
          }
          fr.readAsDataURL(this.videoImageInputTarget.files[0]);
        }
      });
    }
  }

  previewAudioTargetConnected(el) {
    if (this.hasAudioImageInputTarget) {
      this.audioImageInputTarget.addEventListener("change", () => {
        if (FileReader && this.audioImageInputTarget.files && this.audioImageInputTarget.files.length) {
          var fr = new FileReader();
          fr.onload = function () {
            document.getElementById("preview-image").src = fr.result;
          }
          fr.readAsDataURL(this.audioImageInputTarget.files[0]);
        }
      });
    }
  }

  extractionNoticeTargetConnected(el) {
    this.toggleExtractionNotice();
  }

  videoOnlyTargetConnected(el) {
    // No-op in Mux flow; video state managed server-side
  }

  audioOnlyTargetConnected(el) {
    // No-op in Mux flow; audio state managed server-side
  }

  toggleExtractionNotice() {
    if (this.hasExtractionNoticeTarget) {
      if (this.extracting) {
        this.extractionNoticeTarget.classList.remove('hidden');
        if (this.hasPreviewAudioTarget) {
          this.previewAudioTarget.parentElement.classList.add('hidden');
        }
      } else {
        this.extractionNoticeTarget.classList.add('hidden');
      }
    }
  }

  exit(e) {
    if (this._uploading) {
      if (!window.confirm("Exiting now will cancel in progress uploads.\nAre you sure you want to close?")) {
        e.stopImmediatePropagation();
        return;
      }
    }

    window.Turbo.visit(`/dashboard/channels/episodes/${this.episodeIdValue}`);
  }

  async removeAudio(e) {
    const csrfToken = document.getElementsByName("csrf-token")[0].content;

    let formData = new FormData();
    formData.append("authenticity_token", csrfToken);

    const payload = {
      method: "DELETE",
      headers: {
        "Accept": "application/json",
      },
      body: formData
    };

    await fetch(`/dashboard/channels/episodes/${this.episodeIdValue}/remove_audio`, payload);

    if (this.hasPreviewAudioTarget) {
      this.previewAudioTarget.parentElement.classList.add("hidden");
    }
    this.audioOnlyTargets.forEach(t => t.classList.add("hidden"));
  }

  async removeVideo(e) {
    const csrfToken = document.getElementsByName("csrf-token")[0].content;

    let formData = new FormData();
    formData.append("authenticity_token", csrfToken);

    const payload = {
      method: "DELETE",
      headers: {
        "Accept": "application/json",
      },
      body: formData
    };

    await fetch(`/dashboard/channels/episodes/${this.episodeIdValue}/remove_video`, payload);

    if (this.hasPreviewVideoTarget) {
      this.previewVideoTarget.parentElement.classList.add("hidden");
    }
    this.videoOnlyTargets.forEach(t => t.classList.add("hidden"));
  }

  logUploadState(upload_log_id, state, error_message = null) {
    const csrfToken = document.getElementsByName("csrf-token")[0].content;

    let formData = new FormData();
    formData.append("authenticity_token", csrfToken);
    formData.append("state", state);
    formData.append("timestamp", new Date().toISOString());
    formData.append("error_message", error_message);

    fetch(`/dashboard/channels/episodes/${this.episodeIdValue}/log_upload?upload_log_id=${upload_log_id}`, {
      method: "POST",
      headers: {
        "Accept": "application/json",
      },
      body: formData,
    });
  }

  // Extraction helpers

  onExtractionComplete() {
    if (this.hasAddAudioTarget) {
      this.addAudioTarget.classList.add("hidden");
    }
    if (this.hasPreviewAudioTarget) {
      this.previewAudioTarget.parentElement.classList.remove("hidden");
    }
  }

  async checkExtractionStatusAndMaybePoll() {
    try {
      const res = await fetch(`/dashboard/channels/episodes/${this.episodeIdValue}/audio_extraction_status`);
      if (!res.ok) return;
      const data = await res.json();
      const status = data.status;
      if (["queued", "processing"].includes(status)) {
        this.extracting = true;
        this.toggleExtractionNotice();
        this.startExtractionPolling();
      } else {
        this.extracting = false;
        this.toggleExtractionNotice();
        if (status === "done") {
          this.onExtractionComplete();
        }
      }
    } catch (e) { /* noop */ }
  }

  startExtractionPolling() {
    if (this._extractionPollTimer) return;
    const poll = async () => {
      try {
        const res = await fetch(`/dashboard/channels/episodes/${this.episodeIdValue}/audio_extraction_status`);
        if (!res.ok) return;
        const { status } = await res.json();
        if (["queued", "processing"].includes(status)) {
          this.extracting = true;
          this.toggleExtractionNotice();
        } else {
          this.extracting = false;
          this.toggleExtractionNotice();
          if (this.hasAudioExtractionHintTarget) {
            this.audioExtractionHintTarget.classList.add("hidden");
          }
          clearInterval(this._extractionPollTimer);
          this._extractionPollTimer = null;
          if (status === "done") {
            this.onExtractionComplete();
          }
          try {
            if (window.Turbo) {
              window.Turbo.visit(`/dashboard/channels/episodes/${this.episodeIdValue}/detail`, { frame: "step" });
            }
          } catch (_) { }
        }
      } catch (e) { /* noop */ }
    };
    poll();
    this._extractionPollTimer = setInterval(poll, 4000);
  }

  async retryExtraction() {
    const hadRetryTarget = this.hasRetryControlTarget;
    if (hadRetryTarget) {
      this.retryControlTarget.classList.add("hidden");
    }

    try {
      const csrfToken = document.getElementsByName("csrf-token")[0].content;
      let formData = new FormData();
      formData.append("authenticity_token", csrfToken);

      const res = await fetch(`/dashboard/channels/episodes/${this.episodeIdValue}/retry_audio_extraction`, {
        method: "POST",
        headers: { "Accept": "application/json" },
        body: formData
      });
      if (!res.ok) {
        if (hadRetryTarget) this.retryControlTarget.classList.remove("hidden");
        return;
      }

      this.extracting = true;
      this.toggleExtractionNotice();
      this.startExtractionPolling();
    } catch (_) {
      if (hadRetryTarget) this.retryControlTarget.classList.remove("hidden");
    }
  }

  async cancelExtractionOrRemoveAudio() {
    if (this.extracting) {
      try {
        const csrfToken = document.getElementsByName("csrf-token")[0].content;
        let formData = new FormData();
        formData.append("authenticity_token", csrfToken);
        await fetch(`/dashboard/channels/episodes/${this.episodeIdValue}/cancel_audio_extraction`, { method: "POST", body: formData, headers: { "Accept": "application/json" } });
      } finally {
        this.extracting = false;
        this.toggleExtractionNotice();
        if (this.hasAudioExtractionHintTarget) {
          this.audioExtractionHintTarget.classList.add("hidden");
        }
        if (this._extractionPollTimer) { clearInterval(this._extractionPollTimer); this._extractionPollTimer = null; }
      }
    } else {
      await this.removeAudio();
    }
  }

  _onUploadStart(e) {
    this._uploading = true;
    this._totalBytes = e.detail?.file?.size ?? 0;

    this._uploadStats = { startedAt: Date.now(), samples: [] };

    if (this.hasWorkingTarget) {
      this.workingTarget.classList.remove("hidden");
    }

    const file = e.detail?.file;
    if (file) {
      this._localFile = file;
    }
  }

  _onSuccess() {
    this._updateVideoUploadStats("");
    this.commitMuxUpload();
  }

  _onError(e) {
    this._uploading = false;

    if (this.hasErrorTarget && e.detail?.message) {
      this.errorTarget.innerText = e.detail.message;
    }
  }

  _onProgress(e) {
    const percentage = Math.round(e.detail ?? 0);

    if (this.hasUploadPercentageTarget) {
      this.uploadPercentageTargets.forEach(t => t.innerText = `Uploading... ${percentage}%`);
    }
    if (this.hasShowUploadingTarget) {
      this.showUploadingTargets.forEach(t => t.hidden = percentage >= 100);
    }
    if (this.hasShowUploadedTarget) {
      this.showUploadedTargets.forEach(t => t.hidden = percentage < 100);
    }

    this._updateProgressStats(percentage);
  }

  _updateProgressStats(percentage) {
    if (this._totalBytes > 0 && this._uploadStats) {
      const now = Date.now();
      const bytesUploaded = Math.round((percentage / 100) * this._totalBytes);
      const stats = this._uploadStats;

      // Add a sample only when the quantized byte count actually changes (i.e. on each percentage tick).
      // Using a 30-second sliding window avoids the spike-on-tick-boundary problem: speed is computed
      // as (window_end_bytes - window_start_bytes) / window_duration, smoothing over many ticks.
      const lastSample = stats.samples[stats.samples.length - 1];
      if (!lastSample || bytesUploaded > lastSample.b) {
        stats.samples.push({ t: now, b: bytesUploaded });
        const cutoff = now - 30000;
        while (stats.samples.length > 1 && stats.samples[0].t < cutoff) {
          stats.samples.shift();
        }
      }

      let speedBps = 0;
      if (stats.samples.length >= 2) {
        const oldest = stats.samples[0];
        const newest = stats.samples[stats.samples.length - 1];
        const dt = (newest.t - oldest.t) / 1000;
        if (dt > 0) speedBps = (newest.b - oldest.b) / dt;
      }

      const elapsedS = (now - stats.startedAt) / 1000;
      const warmedUp = this._shouldShowStats(elapsedS, bytesUploaded);
      let statsHtml = "";
      if (warmedUp && speedBps > 0 && percentage < 100) {
        const remainingBytes = Math.max(this._totalBytes - bytesUploaded, 0);
        const secondsRemaining = remainingBytes / speedBps;
        statsHtml = `${this._formatBytes(speedBps)}/s, about ${this._formatDuration(secondsRemaining)} left`;
      }
      this._updateVideoUploadStats(statsHtml);
    }

    if (percentage >= 100) {
      this._updateVideoUploadStats("");
    }
  }

  _shouldShowStats(elapsedS, bytesUploaded) {
    return elapsedS >= 3 && bytesUploaded >= 5 * 1024 * 1024;
  }

  _updateVideoUploadStats(statsHtml) {
    if (this.hasUploadStatsTarget) {
      this.uploadStatsTargets.forEach(t => {
        t.innerHTML = statsHtml;
        if (statsHtml) {
          t.classList.remove("hidden");
        } else {
          t.classList.add("hidden");
        }
      });
    }
  }

  _formatBytes(bytes) {
    if (!isFinite(bytes) || bytes <= 0) return "0 B";
    const units = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    const value = bytes / Math.pow(1024, i);
    return `${value >= 100 ? value.toFixed(0) : value >= 10 ? value.toFixed(1) : value.toFixed(2)} ${units[i]}`;
  }

  _formatDuration(totalSeconds) {
    const s = Math.max(0, Math.round(totalSeconds));
    const hours = Math.floor(s / 3600);
    const minutes = Math.floor((s % 3600) / 60);
    const seconds = s % 60;
    if (hours > 0) {
      return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
    }
    return `${minutes}:${String(seconds).padStart(2, "0")}`;
  }
}

UploadController.targets = [
  "muxUploader", "working", "error", "previewVideo", "previewAudio",
  "videoImageInput", "audioImageInput",
  "showUploading", "showUploaded", "uploadPercentage", "showError", "uploadStats",
  "extractionNotice", "videoOnly", "audioOnly", "retryControl", "addAudio", "audioExtractionHint"
];
UploadController.values = { episodeId: Number, dynamic: Boolean, triggerEvent: String };
